/** @format */
import { useEffect, useRef, useCallback } from 'react';
import router from '../Routes/routes';
import {
  clearAuthSession,
  getLastActivity,
  setLastActivity,
} from '../Util/authUtils';

export const IDLE_TIMEOUT_MS = 60 * 60 * 1000;

const ACTIVITY_EVENTS = [
  'mousedown',
  'mousemove',
  'keydown',
  'scroll',
  'touchstart',
  'click',
];

const THROTTLE_MS = 1000;

function useIdleLogout() {
  const lastResetRef = useRef(0);
  const loggedOutRef = useRef(false);

  const lastActivityRef = useRef(getLastActivity() ?? Date.now());

  const logout = useCallback(() => {
    if (loggedOutRef.current) return;

    loggedOutRef.current = true;

    clearAuthSession();
    router.navigate('/login', { replace: true });
  }, []);

  const resetActivityTimer = useCallback(() => {
    const now = Date.now();

    if (now - lastResetRef.current < THROTTLE_MS) {
      return;
    }

    lastResetRef.current = now;
    lastActivityRef.current = now;
    setLastActivity(now);
  }, []);

  useEffect(() => {
    const activityHandler = () => {
      if (loggedOutRef.current) return;

      resetActivityTimer();
    };

    const storageHandler = (event) => {
      if (event.key === 'USER_DETAILS' && event.newValue === null) {
        logout();
      }
    };

    ACTIVITY_EVENTS.forEach((eventName) => {
      window.addEventListener(eventName, activityHandler, {
        passive: true,
      });
    });

    window.addEventListener('storage', storageHandler);

    const interval = window.setInterval(() => {
      if (loggedOutRef.current) return;

      if (!localStorage.getItem('USER_DETAILS')) return;

      const now = Date.now();
      const idleFor = now - lastActivityRef.current;

      if (idleFor >= IDLE_TIMEOUT_MS) {
        logout();
      }
    }, 5000);

    return () => {
      ACTIVITY_EVENTS.forEach((eventName) => {
        window.removeEventListener(eventName, activityHandler);
      });

      window.removeEventListener('storage', storageHandler);
      window.clearInterval(interval);
    };
  }, [logout, resetActivityTimer]);

  useEffect(() => {
    if (!localStorage.getItem('USER_DETAILS')) return;

    const storedActivity = getLastActivity();
    const lastActivity = storedActivity ?? Date.now();

    lastActivityRef.current = lastActivity;
    lastResetRef.current = Date.now();
    setLastActivity(lastActivity);

    if (Date.now() - lastActivity >= IDLE_TIMEOUT_MS) {
      logout();
    }
  }, [logout]);
}

export default useIdleLogout;
